<template>
    <div class="pb-16 sm:pb-20 lg:pb-28">
        <div class="bg-grey-700 theme-dark">
            <div class="container">
                <div class="flex justify-center pt-8 sm:pt-12 mb-5">
                    <ImageBadge
                        :image="model.show.cover_image"
                        :color="model.show.color"
                        :alt="model.show.title"
                        mobile-size="lg"
                        desktop-size="xl"
                    />
                </div>
                <PageHeader
                    :title="`${model.show.title} - ${model.title}`"
                    :subtitle="`${$dayjs(model.date).format('YYYY. MMMM DD, dddd')} - ${model.start_time}-${
                        model.end_time
                    } (${$dayjs(`${model.date} ${model.end_time}`).diff(
                        $dayjs(`${model.date} ${model.start_time}`),
                        'minute'
                    )} perc)`"
                    style="padding-top: 0"
                />
                <div class="text-center pb-12 sm:pb-16 pt-5">
                    <p-button v-if="model.stream_url" :label="playButtonLabel" @click.native="handlePlayButtonClick">
                        <template #icon-left>
                            <pause-icon
                                v-if="
                                    $store.state.player.isPlaying &&
                                    $store.state.player.playingProgram?.slug === model.slug
                                "
                            />
                            <play-icon v-else />
                        </template>
                    </p-button>
                </div>
            </div>
        </div>

        <div class="relative">
            <div class="relative container z-5">
                {{/* * */}}
                {{/* Content */}}
                {{/* * */}}
                <div class="grid grid-cols-12 gap-5 pt-12 sm:pt-16">
                    {{/* * */}}
                    {{/* Left side */}}
                    <div class="col-span-12 lg:col-span-8 order-2 lg:order-1">
                        <img v-if="model.cover_image" class="mb-4 lg:mb-8" :src="model.cover_image" alt="model.title" />

                        <div v-if="model.content" class="tinymce-content mb-16 lg:mb-24" v-html="model.content"></div>

                        <div v-if="model.related_programs.length">
                            <div class="border-b border-grey-200 text-grey-500 pb-2 mb-5 font-FibraOne-UltraBold">
                                További részek
                            </div>
                            <div
                                v-for="(program, i) in model.related_programs"
                                :key="`program-${i}`"
                                class="border-b border-grey-200"
                            >
                                <ProgramCard
                                    :to="`/adasok/${program.slug}`"
                                    :title="model.show.title"
                                    :color="model.show.color"
                                    :description="program.title"
                                    :image="model.show.cover_image"
                                    :interval="`${$dayjs(model.date).format('YYYY. MMMM DD, dddd')} - ${
                                        program.start_time
                                    }-${program.end_time}`"
                                />
                            </div>
                        </div>
                    </div>

                    {{/* * */}}
                    {{/* Right side */}}
                    <div class="col-span-12 lg:col-span-3 lg:col-start-10 order-1 lg:order-2">
                        <div class="grid grid-cols-2 gap-5 lg:gap-16">
                            <div
                                v-if="model.hosts && model.hosts.length"
                                class="col-span-2 sm:col-span-1 lg:col-span-2"
                            >
                                <h3 class="section-title mb-2">Műsorvezetők</h3>
                                <div
                                    v-for="host in model.hosts"
                                    :key="`host-${host.id}`"
                                    class="flex items-center mb-4"
                                >
                                    <ImageBadge
                                        :image="
                                            host.image ||
                                            require('~/assets/images/placeholders/jazzy-thumbnail-fallback_with-red-bg.png')
                                        "
                                        :color="model.color"
                                        :alt="host.name"
                                        desktop-size="sm"
                                        mobile-size="sm"
                                        class="mr-4"
                                    />
                                    <span class="font-FibraOne-UltraBold">{{ host.name }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="bottomBannerData" class="col-span-12 order-3 mb-12 mt-14 lg:mt-20">
                        <BannerCard
                            :title="bottomBannerData.public_title"
                            :label="bottomBannerData.surtitle"
                            :button-text="bottomBannerData.button_text"
                            :slug="bottomBannerData.button_url"
                            :desc="bottomBannerData.content"
                            :image="bottomBannerData.image"
                            :color="bottomBannerData.scheme"
                            :reversed="bottomBannerData.type"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PageHeader from '../../components/Generic/PageHeader/PageHeader';
import ImageBadge from '../../components/UI/ImageBadge';
import ProgramCard from '../../components/Cards/ProgramCard/ProgramCard';
import BannerCard from '../../components/Cards/BannerCard/BannerCard';

export default {
    name: 'Adas',
    components: {
        PageHeader,
        ImageBadge,
        ProgramCard,
        BannerCard,
    },
    async asyncData({ params, $axios, error }) {
        let model = null;

        try {
            await $axios
                .$get(`/program/by-slug/${params.adas}`)
                .then((res) => {
                    if (res.success === 1) {
                        model = res.data;
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        } catch (err) {
            console.log(err);
        }

        if (!model) {
            return error({ statusCode: 404, message: 'Page not found' });
        }

        return { model };
    },
    head() {
        const meta = [
            {
                hid: 'og:title',
                property: 'og:title',
                content: this.model.show.title,
            },
            {
                hid: 'title',
                name: 'title',
                content: this.model.show.title,
            },
            {
                hid: 'og:description',
                property: 'og:description',
                content: this.model.title,
            },
            {
                hid: 'description',
                name: 'description',
                content: this.model.title,
            },
        ];

        if (this.model.og_image) {
            meta.push({
                hid: 'og:image',
                property: 'og:image',
                content: this.model.og_image,
            });
            meta.push({
                hid: 'og:image:url',
                property: 'og:image:url',
                content: this.model.og_image,
            });
        }

        return {
            title: this.model.title,
            meta,
        };
    },
    computed: {
        playButtonLabel() {
            if (
                this.$store.state.player.playingProgram?.slug === this.model.slug &&
                this.$store.state.player.isPlaying
            ) {
                return 'Szünet';
            }
            return 'Lejátszás';
        },
        bottomBannerData() {
            return this.getPromotionBox('program-bottom');
        },
    },
    methods: {
        handlePlayButtonClick() {
            // If the program's data is already loaded into the player, just toggle the player
            if (this.$store.state.player.playingProgram?.slug === this.model.slug) {
                this.$nuxt.$emit('toggleMainPlayerPlayState');
                return;
            }

            // Else we update the player with the selected program.
            // This will result in redundant updates too, but it doesn't do any harm, so it is what it is
            const payload = {};

            payload.src = this.model.stream_url;
            payload.color = this.model.show.color;
            payload.artist = this.model.show.title;
            payload.slug = this.model.slug;
            payload.title = this.model.title;
            payload.image = this.model.show.cover_image;

            this.$store.commit('player/setPlayingChannelName', null);
            this.$store.commit('player/setPlayingProgram', payload);
            this.$nuxt.$emit('updateMainPlayerStreamUrl', payload.src);
            this.$nuxt.$emit('playMainPlayer');
        },
    },
};
</script>

<style scoped></style>
